import React from 'react';
import { OrderItemDetail } from '../../../../api/shop/types/orderTypes';
import { getImageUrl } from '../../../../utils/imageUrl';
import './OtherOrderItems.css';

interface OtherOrderItemsProps {
  orderItems: OrderItemDetail[];
  orderId: string;
  onItemClick?: (orderId: string, orderItemId: string, item: OrderItemDetail) => void;
}

const OtherOrderItems: React.FC<OtherOrderItemsProps> = ({ orderItems, orderId, onItemClick }) => {
  const formatPrice = (price: number) => {
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const getItemStatus = (item: OrderItemDetail): string => {
    if (!item.orderStatusHistory || item.orderStatusHistory.length === 0) {
      return 'CONFIRMED';
    }
    
    // Find all completed statuses (those with updatedAt dates)
    const completedStatuses = item.orderStatusHistory
      .filter(status => status.updatedAt !== null && status.updatedAt !== undefined)
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt!).getTime();
        const dateB = new Date(b.updatedAt!).getTime();
        return dateB - dateA; // Sort by most recent first
      });
    
    // Return the most recent completed status, or 'CONFIRMED' as default
    return completedStatuses.length > 0 ? completedStatuses[0].status : 'CONFIRMED';
  };

  const handleItemClick = (item: OrderItemDetail) => {
    if (!onItemClick || !item._id) return;
    
    const itemStatus = getItemStatus(item);
    onItemClick(orderId, item._id, item);
  };

  return (
    <div className="other-order-items-container">
      <div className="other-items-header">
        <h3 className="other-items-title">Other Items in this order</h3>
      </div>
      
      <div className="other-items-list">
        {orderItems.map((item, index) => {
          // Use stable key based on product name and index to prevent unnecessary re-renders
          const itemKey = `${item.productName}-${index}`;
          // Memoize image URL to prevent recalculation on every render
          const imageUrl = getImageUrl(item.image);
          
          return (
            <div 
              key={itemKey} 
              className={`other-order-item ${onItemClick && item._id ? 'other-order-item-clickable' : ''}`}
              onClick={() => handleItemClick(item)}
              style={onItemClick && item._id ? { cursor: 'pointer' } : {}}
            >
              <div className="other-item-image">
                <img 
                  src={imageUrl}
                  alt={item.productName}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    // Prevent infinite loop - only set placeholder once
                    if (target.src !== '/placeholder-product.png' && !target.dataset.errorHandled) {
                      target.dataset.errorHandled = 'true';
                      target.src = '/placeholder-product.png';
                    }
                  }}
                  loading="lazy"
                />
              </div>
            
            <div className="other-item-details">
              <h4 className="other-item-name">{item.productName}</h4>
              <div className="other-item-meta">
                <span className="other-item-price">{formatPrice(item.price)}</span>
                <span className="other-item-color">
                  {item.color || ''}{item.color ? ' • ' : ''}{(item as any).size || 'UK5'}
                </span>
              </div>
            </div>
            
            <div className="other-item-price-total">
              {formatPrice(item.price * item.quantity)}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default OtherOrderItems;